import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_PROFILE_SECTION_PATHS, Url, CHECKOUT_IDS } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';
import {
  withVerification,
  WithVerificationProps,
} from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import { actions as modalActions } from '@wix/da-shared-react/pkg/Modals';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import ShareIcon from '@wix/da-ds/pkg/Icons/24x24/Share';
import CakeIcon from '@wix/da-ds/pkg/Icons/16x16/BirthdayCake';
import LlamaIcon from '@wix/da-ds/pkg/Icons/Llama';
import PointsIcon from '@wix/da-ds/pkg/Icons/Points';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { NullableDropdownItem } from '@wix/da-shared-react/pkg/Dropdown/types';
import BlockIcon from '@wix/da-ds/pkg/Icons/24x24/Block';
import TierIcon from '@wix/da-ds/pkg/Icons/24x24/Tier';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import {
  ProfileOwnerWatchButton,
  ProfileOwnerSubscribeButton,
} from '@wix/da-shared-react/pkg/Button';
import IconButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import { RestrictLoggedOutPayload } from '@wix/da-shared-react/pkg/redux/loggedout/actionCreators';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import BaseSubNavBarDesktop, {
  Props as BaseProps,
} from '../../BaseSubNavBarDesktopInner';
import WatchButtonConnectContainer from '../../../WatchButton.container';
import RightSideButtonWrapper from '../../BaseSubNavBarDesktopInner/legos/RightSideButtonWrapper';
import LargeButtonsWrapper from '../../BaseSubNavBarDesktopInner/legos/LargeButtonsWrapper';
import { ProfileOwnerGiveState } from '../../../../../../types/profileOwner';
import ProfileSkinsButton from '../_partials/ProfileSkinsButton';
import ShareButton from './_partials/ShareButton';
import GiveButton from './_partials/GiveButton';
import { CollapsingElementsContainer } from '../../CollapsingElements';
import { CollapsingElementId } from '../../CollapsingElements/types';
import NoteButton from './_partials/NoteButton';
import s from './UserProfileSubNavDesktopInner.scss';

const WatchButton = WatchButtonConnectContainer(ProfileOwnerWatchButton);

export interface Props extends BaseProps {
  profileOwnerUser: PapiUser;
  isOwner: boolean;
  isLoggedIn: boolean;
  hasSubscriptions: boolean;
  isAuthorizedToCustomize: boolean;
  isProfileOwnerWatchingCurrentUser: boolean;
  giveState?: ProfileOwnerGiveState;
  initGiveState: () => void;
  blockProfileOwner: () => void;
  showShareLinkPopup: () => void;
  restrictLoggedOut: (payload: RestrictLoggedOutPayload) => void;
  pushBadgeModal: typeof modalActions.pushBadgeModal;
  onLlamaBadgeSent: () => void;
  pushPointsModal: (username: string, title: string) => void;
}

enum COLLAPSING_BUTTON {
  MESSAGE = 'message',
  SHARE = 'share',
  GIVE = 'give',
  SUBSCRIPTIONS = 'subscriptions',
}

const UserProfileSubNavDesktopInner: React.FC<
  Props & WithVerificationProps
> = ({
  isSticky,
  profileOwnerUser,
  isOwner,
  isLoggedIn,
  isAuthorizedToCustomize,
  isProfileOwnerWatchingCurrentUser,
  isVerificationNeeded,
  pushVerificationPopup,
  blockProfileOwner,
  restrictLoggedOut,
  hasSubscriptions,
  showShareLinkPopup,
  pushBadgeModal,
  onLlamaBadgeSent,
  pushPointsModal,
  giveState,
  initGiveState,
}) => {
  const { t } = useTranslation();
  const newSubscribeSubnav = useFlag('subscribe_subnav');
  const [collapsedIds, setCollapsedIds] = useState<CollapsingElementId[]>([]);
  const messageButtonRef = useRef<HTMLDivElement | null>(null);
  const shareButtonRef = useRef<HTMLDivElement | null>(null);
  const giveButtonRef = useRef<HTMLDivElement | null>(null);
  const subscriptionsButtonRef = useRef<HTMLDivElement | null>(null);

  const showNewSubscribeButton =
    newSubscribeSubnav && !isOwner && hasSubscriptions;

  const hideWatchButton = newSubscribeSubnav && profileOwnerUser.isSubscribed;
  const hideGiveButton = newSubscribeSubnav ? isSticky : false;

  const collapsingButtons = [
    {
      id: COLLAPSING_BUTTON.MESSAGE,
      ref: messageButtonRef,
    },
    {
      id: COLLAPSING_BUTTON.SHARE,
      ref: shareButtonRef,
    },
    {
      id: COLLAPSING_BUTTON.GIVE,
      ref: giveButtonRef,
    },
    {
      id: COLLAPSING_BUTTON.SUBSCRIPTIONS,
      ref: giveButtonRef,
    },
  ];

  const collapsedButtonsAsMenuItems = {
    [COLLAPSING_BUTTON.MESSAGE]: {
      icon: <MailIcon />,
      label: t(`navigation.subnavigation.message`),
      onClick: isVerificationNeeded
        ? () => pushVerificationPopup('notes')
        : undefined,
      link: isVerificationNeeded
        ? undefined
        : Url.userNoteLink(profileOwnerUser.username),
    },
    [COLLAPSING_BUTTON.SHARE]: {
      icon: <ShareIcon />,
      label: t(`navigation.subnavigation.share`),
      onClick: showShareLinkPopup,
    },
    [COLLAPSING_BUTTON.SUBSCRIPTIONS]: {
      icon: <TierIcon />,
      label: t(`navigation.route.var.subscriptions`),
      link: Url.userLink(
        profileOwnerUser.username,
        `${USER_PROFILE_SECTION_PATHS.SUBSCRIPTIONS}#tiers`
      ),
    },
  };

  const renderSendMessageButton = () => {
    let onClick: (() => void) | undefined = undefined;
    if (!isLoggedIn) {
      onClick = () =>
        restrictLoggedOut({ restrictedActionName: ReferralType.NOTE });
    } else if (isVerificationNeeded) {
      onClick = () => pushVerificationPopup('notes');
    }
    const href = Url.userNoteLink(profileOwnerUser.username);
    return (
      <SolidButton
        size="large"
        variant="main"
        title={t(`navigation.subnavigation.message`)}
        biData={{
          evid: BiEventType.SEND_NOTE_CLICK,
          recipientid: profileOwnerUser.useridUuid,
        }}
        {...(onClick && { onClick })}
        {...(!onClick && { href })}
      >
        {t(`navigation.subnavigation.message`)}
      </SolidButton>
    );
  };

  const renderWatchButton = () => {
    return (
      <WatchButton
        className={s['watch-button']}
        allowUnsubscribe={false}
        isWatchingBack={isProfileOwnerWatchingCurrentUser}
      />
    );
  };

  const getMoreActionsMenuItems = () => {
    const menuItems: Array<NullableDropdownItem> = [];

    if (collapsedIds.length) {
      const collapsedIntoMenuItems = collapsedIds.map(
        id => collapsedButtonsAsMenuItems[id]
      );
      menuItems.push(...collapsedIntoMenuItems);
    }

    if (isSticky && newSubscribeSubnav) {
      const giveMenuGroup: NullableDropdownItem = { items: [] };

      const coreLabel = giveState?.canBeGivenCore
        ? t('navigation.subnavigation.giveCore')
        : t('navigation.subnavigation.hasCore');
      const giveCoreUrl = Url.giveCoreMembership(profileOwnerUser.username);
      const biData = {
        evid: BiEventType.GIFT_SELECT_CLICK,
        recipientid: profileOwnerUser.useridUuid,
      };

      giveMenuGroup.items.push({
        icon: <CoreSymbol corePackage="core_access" size={IconSize.MEDIUM} />,
        label: giveState?.isLoading
          ? t('navigation.subnavigation.checkingCoreStatus')
          : coreLabel,
        biData: {
          ...biData,
          menu_item_name: coreLabel,
          link_url: giveCoreUrl,
        },
        onClick: () => window.location.assign(giveCoreUrl),
        disabled: !giveState?.canBeGivenCore,
      });

      const pointsLabel = t('navigation.subnavigation.givePoints');
      giveMenuGroup.items.push({
        icon: <PointsIcon />,
        label: pointsLabel,
        biData: { ...biData, menu_item_name: pointsLabel },
        onClick: () => {
          pushPointsModal(
            profileOwnerUser.username,
            t(`navigation.subnavigation.givePoints`)
          );
        },
      });

      const llamaLabel = giveState?.canBeGivenLlama
        ? t('navigation.subnavigation.giveLlamaBadge')
        : t('navigation.subnavigation.hasGotLlamaBadge');

      giveMenuGroup.items.push({
        icon: <LlamaIcon />,
        label: llamaLabel,
        biData: { ...biData, menu_item_name: llamaLabel },
        onClick: () => {
          pushBadgeModal(profileOwnerUser.username, onLlamaBadgeSent);
        },
      });

      const cakeLabel = giveState?.canBeGivenCake
        ? t('navigation.subnavigation.giveCakeBadge')
        : t('navigation.subnavigation.hasGotCakeBadge');

      giveMenuGroup.items.push({
        icon: <CakeIcon />,
        label: cakeLabel,
        biData: { ...biData, menu_item_name: cakeLabel },
        onClick: () => {
          window.location.assign(
            `${Url.checkoutLink(CHECKOUT_IDS.CAKE)}&foruserid=${
              profileOwnerUser.userId
            }`
          );
        },
      });

      menuItems.push(giveMenuGroup);
    }

    // subscribe_subnav places share and block in the same menu group
    const shareBlockMenuGroup: NullableDropdownItem = { items: [] };
    const itemContainer = newSubscribeSubnav
      ? shareBlockMenuGroup.items
      : menuItems;

    itemContainer.push({
      icon: <ShareIcon />,
      label: t(`navigation.subnavigation.share`),
      onClick: showShareLinkPopup,
    });

    if (isLoggedIn && !isOwner) {
      itemContainer.push({
        icon: <BlockIcon />,
        label: t('navigation.subnavigation.blockUser'),
        onClick: blockProfileOwner,
      });
    }

    if (newSubscribeSubnav) {
      menuItems.push(shareBlockMenuGroup);
    }

    return menuItems;
  };

  const renderMoreActionsIconDropdown = () => {
    const menuItems = getMoreActionsMenuItems();
    if (!menuItems.length) {
      return null;
    }

    return (
      <RightSideButtonWrapper>
        <IconButtonWithContextMenu
          items={menuItems}
          withButton
          onOpen={newSubscribeSubnav ? initGiveState : undefined}
        >
          <EllipsisIcon withPadding />
        </IconButtonWithContextMenu>
      </RightSideButtonWrapper>
    );
  };

  const rightSideContent = isOwner ? (
    isAuthorizedToCustomize && (
      <RightSideButtonWrapper>
        <ProfileSkinsButton />
      </RightSideButtonWrapper>
    )
  ) : (
    <>
      {renderMoreActionsIconDropdown()}
      {!newSubscribeSubnav && (
        <RightSideButtonWrapper
          innerRef={shareButtonRef}
          isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.SHARE)}
        >
          <ShareButton profileOwner={profileOwnerUser} />
        </RightSideButtonWrapper>
      )}

      {!hideGiveButton && (
        <RightSideButtonWrapper
          innerRef={giveButtonRef}
          isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.GIVE)}
        >
          <GiveButton />
        </RightSideButtonWrapper>
      )}

      {newSubscribeSubnav && (
        <RightSideButtonWrapper
          innerRef={messageButtonRef}
          isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.MESSAGE)}
        >
          <NoteButton profileOwner={profileOwnerUser} />
        </RightSideButtonWrapper>
      )}
      <LargeButtonsWrapper>
        {!newSubscribeSubnav && (
          <RightSideButtonWrapper
            innerRef={messageButtonRef}
            isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.MESSAGE)}
          >
            {renderSendMessageButton()}
          </RightSideButtonWrapper>
        )}
        {!hideWatchButton && (
          <RightSideButtonWrapper>{renderWatchButton()}</RightSideButtonWrapper>
        )}
        {showNewSubscribeButton && (
          <RightSideButtonWrapper
            innerRef={subscriptionsButtonRef}
            isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.SUBSCRIPTIONS)}
          >
            <ProfileOwnerSubscribeButton user={profileOwnerUser} />
          </RightSideButtonWrapper>
        )}
      </LargeButtonsWrapper>
    </>
  );
  return (
    <BaseSubNavBarDesktop
      isSticky={isSticky}
      rightSideContent={
        <CollapsingElementsContainer
          collapsingElements={collapsingButtons}
          onCollapsedIdsChange={setCollapsedIds}
          justifyContent="flex-end"
        >
          {rightSideContent}
        </CollapsingElementsContainer>
      }
    />
  );
};
UserProfileSubNavDesktopInner.displayName = 'UserProfileSubNavDesktopInner';

export default withVerification(UserProfileSubNavDesktopInner);
